// English translations
import { TranslationKeys } from './zh';

export const en: TranslationKeys = {
  // Navigation
  nav: {
    aboutMe: 'About Me',
    challenge100: 'Challenge 100',
    spy100: 'Spy 100',
    launch100: 'Launch 100',
    ship100: 'Ship 100',
    grow100: 'Grow 100',
    projects: 'Projects',
    blog: 'Blog',
    contact: 'Contact',
    resources: 'Resources',
    pearl: 'Pearl Framework',
    methodology: 'Methodology',
    faq: 'FAQ',
  },
  
  // Hero
  hero: {
    title1: 'From System Crash',
    title2: 'To Self Reconstruction',
    subtitle: 'Wang Haisheng · Lighting your path with 40 years of lessons learned',
    soundOn: 'Sound On',
  },
  
	  // Services/Projects
	  services: {
	    badge: 'Recent Work',
	    title: 'Featured Projects',
	    subtitle: 'Selected project showcase',
	    viewMore: 'View More',
	    darkroomNote:
	      'Each piece is carefully developed in our creative darkroom, ensuring every detail captures the essence of your vision with precision and artistic flair.',
	  },

	  // Home: Portfolio / personal intro section
	  portfolio: {
	    badge: 'About Me',
	    title: "Hi, I'm Wang Haisheng",
	    subtitle:
	      "An explorer who turned life's 'bugs'—from bedwetting to entrepreneurship—into product 'features'.",
	    video: {
	      title: 'Self-introduction by Wang Haisheng',
	      url: 'https://www.youtube.com/embed/YOUR_VIDEO_ID',
	      caption: '3 minutes to get to know my story: from system crash to self reconstruction.',
	    },
	    identities: {
	      title: 'My Three Identities',
	      trauma: {
	        title: 'Trauma Explorer',
	        description:
	          'From preschool bedwetting to chronic hives at 30, four decades of physical and emotional struggles have turned me into my own researcher.',
	      },
	      product: {
	        title: 'Product Manager',
	        description:
	          '12 years in AI + healthcare, learning how to simplify complex problems from data across 800+ hospitals.',
	      },
	      entrepreneur: {
	        title: 'Entrepreneur',
	        description:
	          "Since 2024, I've been productizing my personal experiences, believing that each person's pain can become light for someone else.",
	      },
	    },
	    story: {
	      p1: "I'm not a success-guru or a feel-good spiritual influencer. I'm just an ordinary person who has been wrestling with all kinds of 'system failures' in life for 40 years.",
	      p2: "From the shame of childhood bedwetting to the torment of adult hives, from technical challenges at work to the inner work of trauma healing—I discovered that every breakdown is the beginning of the next rebuild.",
	      p3: "Now, I've turned these 'life bugs' into reusable 'patches', hoping to light a small lamp for those who are also fumbling through the dark.",
	    },
			    stats: {
			      yearsProduct: { value: '12+', label: 'Years of product trial and error' },
			      hospitals: { value: '800+', label: 'Hospitals whose wild requirements I have seen' },
			      yearsTrauma: { value: '40', label: 'Years on the trauma exploration path' },
			      rebuilds: { value: '', label: 'Times rebuilding after a breakdown' },
			    },
	  },

	  // Home: Social / channels section (Awards)
	  social: {
	    badge: 'Social & Channels',
	    title: 'Social & Channels',
	    subtitle: 'Find and follow me across different platforms.',
	  },

		  // Home: Life timeline section (About Timeline)
		  aboutTimeline: {
		    badge: 'My Journey',
		    title: 'Life Timeline',
		    subtitle: 'A 12-year journey from medical AI to trauma exploration',
		    steps: [
		      {
		        title: '2012 · AI Beginnings',
		        description: 'Entered the AI field and built product lines from 0 to 1.',
		      },
		      {
		        title: '2014 · Shift to Healthcare',
		        description: 'Moved into healthcare and began my career as a product manager.',
		      },
		      {
		        title: '2017 · MedTech Entrepreneurship',
		        description: 'Led intelligent ultrasound products and pushed forward AI + healthcare integration.',
		      },
		      {
		        title: '2020 · Trauma Exploration',
		        description: 'Started deep self-healing and inner exploration.',
		      },
		      {
		        title: '2024 · Productizing Experience',
		        description: 'Created borninsea to turn personal experiences into products.',
		      },
		    ],
		    stats: {
		      experience: '12 years of experience',
		      domain: 'AI + Healthcare',
		      philosophy: 'Continuous iteration',
		    },
		  },
  
  // About page
  about: {
    backHome: 'Back to Home',
    title: 'About Me',
    subtitle: 'I am multifaceted. Each facet carries different stories, different missions, different versions of me.',
    quote: '"We don\'t need to be perfect. We just need to recognize the \'viruses\' and \'vulnerabilities\' in our system, then learn to coexist with them, repair them, and ultimately make them work for us."',
    
    identities: {
      trauma: {
        title: 'Trauma Explorer',
        subtitle: 'Trauma Explorer',
        description: 'Forty years of journey and awakening, from family of origin to complete collapse and reconstruction of self-relationship. I\'ve distilled this journey into a resilience coaching system, open-source complex trauma dataset, and trauma alchemy workshops.',
      },
      entrepreneur: {
        title: 'Entrepreneur',
        subtitle: 'Entrepreneur',
        description: 'A 0-to-1 product builder, focused on productizing personal experiences and professional knowledge. Exploring personal IP and digital products in the AI era.',
      },
      resume: {
        title: 'Professional Resume',
        subtitle: 'Resume',
        description: '985 Master\'s degree, cross-domain explorer. A diverse career path from academia to practice, from technology to humanities.',
      },
    },
    
    // Trauma Explorer page
    trauma: {
      title: 'Trauma Explorer',
      subtitle: 'Trauma Explorer',
      greeting: 'Hello everyone, good evening.',
      sections: {
        beginning: {
          title: 'My life began with a separation',
          content: [
            'My parents divorced when I was just over a year old. My birth mother has intellectual disabilities, and my father is disabled. He also had no father—my grandmother became a widow at three and raised six sons alone. My stepmother is a good person, but she also lost her mother at a young age and was raised by her father. All three of them are farmers who cannot read.',
            'What does this mean?',
            'It means that from my earliest memories, I never knew what it felt like to be "cared for by a mother." I didn\'t know the warmth of being hugged, didn\'t know whose arms to run to when I was hurt. In elementary school music class, tears would fall on their own when I heard "Only Mother is Good in the World."'
          ]
        },
        slowStart: {
          title: 'My life started in slow motion',
          content: [
            'Others walked at one year old; I couldn\'t stand steady at three. Others spoke at two; I didn\'t utter clear words until five.',
            'My childhood was filled with embarrassment. In preschool, I had several accidents in my pants. I wet the bed at night until I was twelve. I can\'t remember how many mornings I woke up in cold, shameful dampness.',
            'My body seemed unable to understand the world\'s language. For over thirty years, I\'ve had trembling hands almost daily and frequent diarrhea. So I never dared eat popsicles or drink beverages others offered me.'
          ]
        },
        silence: {
          title: 'I was the most silent child in the family',
          content: [
            'A single angry look from my father would make me feel suffocated. It took me thirty years to dare make a simple request to my parents.'
          ],
          list: [
            'Nearly drowned in a pond, didn\'t dare tell.',
            'Thumb nearly severed by glass, shoes soaked with blood, came home without saying a word.',
            'Mocked, bullied, and oppressed at school—parents would never know your pain.',
            'Always hungry in high school boarding, didn\'t grow for three years, they seemed not to notice.',
            'I don\'t know what it feels like to have someone bring an umbrella on a rainy day.'
          ],
          conclusion: [
            'The first thing I learned in life was to talk to myself—on the road, in rooms, in every moment without response.',
            'These aren\'t stories—they\'re the reality I had to swallow every single day.'
          ]
        },
        escape: {
          title: 'I thought hard work could help me escape',
          content: [
            'I thought if I worked hard enough and ran far enough, I could leave all this behind.',
            'With this slow, leaky body, I walked for forty years. I made it to a 985 university, earned a master\'s degree. Through my own efforts, I renovated that heartbreaking northwestern earthen house back home into a place where my parents could live peacefully.',
            'But later, I discovered with horror: my body had left that home, but my heart hadn\'t. It was dragged by programmed scripts into another more hidden, longer nightmare.'
          ]
        },
        relationships: {
          title: 'Replication in intimate relationships',
          content: [
            'When I began building my own intimate relationships, I copied the entire script from childhood. Unconsciously, I chose a relationship full of neglect, depletion, and impossible communication. Like a programmed robot, I equated all love with "one-way, painful, prolonged giving." I paid all costs, endured all coldness, firmly believing "this is my value, this is the love I deserve."',
            'I watched myself clearly being drained like a battery, cracking like a dam. I accurately predicted the ending but never thought of leaving. Because leaving meant betraying all my past "giving," meant the entire "responsible" meaning of life I had constructed would instantly collapse.'
          ],
          quote: 'You see, this is the most cunning aspect of emotional trauma. It doesn\'t just make you suffer—it distorts your understanding of "love" and "value," making you willingly and actively repeat that pain, treating it as your only destiny and badge of honor.'
        },
        sharing: {
          title: 'Why share all this',
          content: [
            'Today, I\'m laying out these most private, most vulnerable "life malfunction reports" and "emotional program bugs" completely open.',
            'Not to get a "how pitiful" response.',
            'But to let you see: how trauma infects our choices like a virus, hijacking our lives.',
            'More importantly, to tell you: if even someone like me—with complete system failure from body to family to adult intimate relationships—can step by step complete "system debugging" and "program rewriting," then the maze you feel trapped in might really just be some implanted error code.'
          ]
        },
        threeLights: {
          title: 'Three lights',
          intro: 'I\'ve walked this dark road for forty years, experiencing complete collapse and reconstruction from family of origin to self-relationship. Now, I want to turn back and light three lamps for those still spinning in similar mazes.',
          lights: [
            {
              name: 'Seeing',
              description: 'Help you see clearly that the path beneath your feet is full of ancient code traps.'
            },
            {
              name: 'Diagnosis',
              description: 'Give you tools to analyze where exactly your "emotional program" has been hijacked.'
            },
            {
              name: 'Rewriting',
              description: 'Point to your scars and tell you: not only can this heal, you can gain source code access and personally write future scripts.'
            }
          ]
        },
        conclusion: {
          message: [
            'If you\'ve also curled up in the dark night, not knowing where the pain comes from.',
            'If you\'ve also exhausted yourself in relationships, feeling you have no choice.',
            'If you also feel there\'s a voice inside repeating familiar tragic scripts.',
            'Then please, look at me.'
          ],
          proof: [
            'I\'m not your role model—I\'m a "proof of concept."',
            'A small, concrete piece of evidence: proving that even someone whose initial programming was written by the deepest trauma can still, through difficult self-debugging, reclaim editing rights to their life. And with these hands that have repaired the system, provide a detailed "technical manual" for those who come after.'
          ],
          finalQuote: 'We don\'t need to be perfect. We just need to recognize the "viruses" and "vulnerabilities" in our system, then learn to coexist with them, repair them, and ultimately make them work for us.',
          invitation: [
            'If you also feel you start too slowly, have too many bugs, keep repeating the same damn mistakes.',
            'Then please, remember how I look right now.',
            'Follow me.',
            'I\'ve completed the full cycle from collapse to reclaiming power.',
            'For the road ahead, if you\'re willing, I can be your technical consultant.',
            'Together, let\'s debug life and rewrite code.',
            'Good night.'
          ]
        },
        cta: 'View my trauma exploration products'
      }
    },
    
    // Entrepreneur page
    entrepreneur: {
      title: 'Entrepreneur',
      subtitle: 'Entrepreneur',
      intro: [
        'I\'m an explorer who emerged from physical struggles. From preschool to sixth grade, I wet the bed almost daily. My stomach was so sensitive that I barely dared drink beverages before college graduation and had eaten only single-digit popsicles. These "common, non-life-threatening ailments" made me constantly wonder: what\'s wrong with me?',
        'In 2018, my spirit shattered and hives arrived as expected. After searching online and taking loratadine for over two years with no results, I chose to research myself—reading extensively through CNKI and finding Yupingfeng San. Symptoms improved significantly after two weeks and stabilized after three months. This experience made me realize: the power of exploration.',
        'Coming to Xi\'an in 2022, encountering Treatise on Cold Damage and Zhuangzi, I began to realize the enormous psychological debt from 30 years of self-blame and self-denial. Accepting myself, accepting everything that happened—this shift in consciousness became the origin of my entrepreneurship.'
      ],
      keyProjects: {
        title: 'Key Projects',
        projects: [
          {
            title: 'heytcm',
            year: '2024',
            description: 'A mind-body cultivation exploration platform based on Traditional Chinese Medicine theory, helping people tormented by chronic symptoms find paths to self-healing'
          },
          {
            title: 'Yupingfeng San Self-healing Practice',
            year: '2018-2020',
            description: 'Through independent research of TCM literature, found a treatment plan for hives, reducing episodes from monthly to once every six months'
          },
          {
            title: 'Sleep Ring Concept Design',
            year: '2023-2024',
            description: 'A circadian rhythm management solution based on smart rings, answering users\' questions "What\'s wrong with me?" and "What should I do?"'
          }
        ]
      },
      principles: {
        title: 'Entrepreneurial Principles',
        items: [
          {
            title: 'Personal Experience as Product',
            description: 'Transform unique life experiences into replicable, shareable knowledge products'
          },
          {
            title: 'Small Steps, Fast Iteration',
            description: 'Validate ideas with minimal cost, iterate quickly, optimize continuously'
          },
          {
            title: 'AI Native',
            description: 'Fully leverage AI tools to improve efficiency, explore new paradigms of human-AI collaboration'
          }
        ]
      },
      ventures: {
        title: 'Project Matrix',
        items: [
          {
            title: 'Large 100',
            description: 'Large project incubation plan, focusing on product ideas with scale potential',
            status: 'In Progress'
          },
          {
            title: 'Launch 100',
            description: 'Rapid launch plan, validating market demand with minimum viable products',
            status: 'In Progress'
          },
          {
            title: 'Spy 100',
            description: 'Market research and competitive analysis, discovering hidden business opportunities',
            status: 'In Progress'
          },
          {
            title: 'Ship 100',
            description: 'Product delivery plan, complete process from idea to launch',
            status: 'In Progress'
          },
          {
            title: 'Grow 100',
            description: 'Growth experiment plan, exploring sustainable user acquisition strategies',
            status: 'In Progress'
          }
        ]
      },
      philosophy: {
        title: 'My Entrepreneurial Philosophy',
        content: [
          'Focusing on the quantification of qi—yin-yang—five elements is destined to be a difficult path. But I believe personal struggles can be transformed into products to help more people.',
          'There\'s an English phrase "move on" that I really like. The focus should be on how I want to be, what I can do in the future. Accept yourself, accept everything that happens.',
          'At the end of 2024, I launched the heytcm project, hoping to explore mind-body cultivation with more people, especially those tormented by chronic symptoms.'
        ]
      }
    },
    
    // Resume page
    resume: {
      title: 'Wang Haisheng',
      subtitle: 'AI + Healthcare Product Expert · 12 Years Experience',
      contact: {
        phone: '18926010461',
        email: 'edwin_uestc@163.com',
        github: 'wanghaisheng'
      },
      summary: 'AI domain expert with 12 years of product management experience, specializing in technology application and innovation in the healthcare industry. Skilled at applying AI technology to product design and development, optimizing user experience, with deep product data analysis capabilities.',
      highlights: {
        title: 'Core Strengths',
        items: [
          '12 years of medical informatization industry experience, deep expertise in AI + healthcare',
          'Comprehensive AI technology application experience: NLP, OCR, computer vision, time series data analysis',
          'Excellent team management and cross-departmental collaboration skills, managed 20+ cross-functional teams',
          'Deep healthcare industry knowledge, led product deployment and data integration for 800+ hospitals'
        ]
      },
      skills: {
        title: 'Professional Skills',
        categories: [
          {
            name: 'AI Technology',
            items: ['NLP', 'OCR', 'Computer Vision', 'Pose Recognition', 'Time Series Analysis', 'Machine Learning']
          },
          {
            name: 'Healthcare Domain',
            items: ['Electronic Health Records', 'Medical Imaging', 'Medical Devices', 'Health Management', 'HL7', 'FHIR']
          },
          {
            name: 'Product Management',
            items: ['Requirements Analysis', 'Product Planning', 'Prototype Design', 'Project Management', 'Agile Development']
          },
          {
            name: 'Technical Architecture',
            items: ['Microservices', 'Cloud Platforms', 'Big Data Processing', 'API Design', 'Python']
          }
        ]
      },
      timeline: {
        title: 'Career Journey',
        experiences: [
          {
            year: '2022',
            endYear: 'Present',
            company: 'Xi\'an Tianhe Defense Technology Co., Ltd.',
            role: 'Product Manager',
            highlights: [
              'Responsible for full lifecycle management of AI-driven smart health watch products from 0 to 1',
              'Led TCM smart watch feature definition and APP design',
              'Completed entire process from requirements analysis to prototype mass production within 6 months',
              'Developed AI-based sleep quality assessment models and personalized treatment recommendation engines'
            ],
            project: {
              name: 'Tianhe Yunmai Smart Watch',
              desc: 'Read 100+ papers, researched 10+ competitors, completed core algorithm SDK development in 4 months'
            }
          }
        ]
      },
      education: {
        title: 'Education',
        school: 'University of Electronic Science and Technology of China',
        degree: 'Master\'s · Biomedical Engineering',
        period: '2005 - 2012'
      },
      achievements: {
        title: 'Honors & Certifications',
        items: [
          'Outstanding Contribution Award from Open Medical and Health Alliance',
          'Initiator of FHIR Standard Chinese Version',
          'International HL7 V2.7, V3RIM, V3CDA Standard Certified Expert',
          'Maintains multiple open source projects on GitHub (OCR microservices, automation, social media monitoring, etc.)'
        ]
      }
    }
  },
  
  // Contact
  contact: {
    badge: 'Let\'s Chat',
    title: 'Talk to Me',
    subtitle: 'Book a conversation, share your ideas, and let\'s explore together',
    bookingTitle: 'Book a Call',
    bookingDetails: '30 minutes • Video Call • Free Consultation',
    available: 'Available Now',
    
    cards: {
      share: {
        title: 'Share Ideas',
        description: 'Talk about your challenges, ideas, or directions you\'re exploring',
      },
      explore: {
	        title: 'Share Experience',
	        description: 'I\'ll share my learnings from trauma healing and the entrepreneurial journey',
      },
      connect: {
	        title: 'Explore Possibilities',
	        description: 'Let\'s see whether we can create something interesting together',
      },
    },
	  },
	  
	  // 100-series collection page
	  collections: {
	    challenge100: {
	      title: 'Challenge 100 System',
	      subtitle: '100 practical experiments across Spy, Launch, Ship and Grow',
	      intro:
	        'A single overview for Spy 100, Launch 100, Ship 100 and Grow 100, showing how far each project has travelled along the journey.',
	      metrics: {
	        totalLabel: 'Total projects now',
	        ofHundred: 'Goal: 100 per stage',
	      },
	      stages: {
	        spy: {
	          title: 'Spy 100 · Research & Intelligence',
	          description:
	            'Watching trends, collecting ideas, and validating signals with minimal cost.',
	        },
	        launch: {
	          title: 'Launch 100 · Fast Launch',
	          description:
	            'Ship the smallest viable version to get real-world feedback quickly.',
	        },
	        ship: {
	          title: 'Ship 100 · Real Delivery',
	          description:
	            'Build versions that are truly usable, reliable, and can be depended upon.',
	        },
	        grow: {
	          title: 'Grow 100 · Sustainable Growth',
	          description:
	            'Discover repeatable growth mechanisms and move projects into compounding mode.',
	        },
	      },
	      narrative: {
	        intro: {
	          title: "From 'What if?' to 'What is.'",
	          body:
	            "The 'launch100' project isn't about building a business. It's an answer to a personal question: what can one person, starting from zero, create with the incredible power of modern AI? It's a journey of pure exploration, driven by curiosity.\n\nThis is a testament to the 'Launch vs. Ship' philosophy. We're not shipping perfect, market-ready products. We are launching ideas, experiments, and possibilities. Each of the 100 apps is a stepping stone in a public learning process, proving that the barrier to creation has never been lower.",
	        },
	        spy: {
	          title: 'The Bridge to Shipping: Spy 100',
	          body:
	            "While 'launching' is an act of personal exploration, 'shipping' is a commitment to the market. Before you can successfully 'Ship,' you must first 'Spy.' This is the critical stage of research and reconnaissance, designed to uncover genuine user needs instead of building on assumptions. The 'Spy 100' challenge is about studying potential competitors and market leaders to truly understand the landscape.\n\nThis involves analyzing their traffic acquisition strategies, investigating their Google search performance (SEO), observing their social media operations, and identifying the strengths and weaknesses of their product design. It's the essential first step from a launched idea to a shippable product.",
	          caption: 'Analysis of data and competitors',
	        },
	        ship: {
	          title: 'The Next Chapter: Ship 100',
	          body:
	            "To 'launch' is to explore an idea. To 'ship' is to commit to it. The 'Ship 100' challenge is the next step: taking the most promising concepts from the launchpad and forging them into polished, user-centric products.\n\nIt's about moving from 'what if' to 'what is,' focusing on solving real problems, refining the user experience, and delivering tangible value. This is the journey from a personal project to a product people can rely on.",
	          caption: 'A polished product being delivered',
	        },
	        grow: {
	          title: 'The Ultimate Goal: Grow 100',
	          body:
	            "Beyond shipping a product is the challenge of growing it. 'Grow 100' represents the ambition to build a sustainable ecosystem around an idea—to foster a community, create lasting value, and make a genuine impact.\n\nThis is the long-term vision: transforming a single spark of an idea into a thriving, self-sustaining entity that continues to evolve and serve its users. It’s the final frontier of the indie developer's journey.",
	        },
		      },
		      journey: {
		        title: 'Challenge 100 Journey',
		        subtitle: "A timeline of every project's first launch and key milestones, across Spy, Launch, Ship and Grow.",
		        startLabel: 'Started',
		        endLabel: 'Completed',
		        noDate: 'Dates to be filled in soon.',
		        allProjects: 'All Projects Timeline',
		        allProjectsSubtitle: 'View all projects in chronological order',
		        scrollHint: 'Scroll down to see more projects',
		        projectCount: 'projects',
		      },
		      cta: {
		        title: 'Ready to dive deeper?',
		        subtitle: 'Jump into the full Challenge 100 overview, or walk through the journey timeline across all four chapters.',
		        primary: 'Dive into Challenge 100',
		        secondary: 'View Journey Timeline',
		      },
		    },
		  },
	  
  // Footer
  footer: {
    description:
      'Productizing personal experiences, exploring the possibilities of mind-body cultivation and entrepreneurship with AI-era tools and methodologies.',
    toolsTitle: 'The Technology Core',
    toolsDescription:
      'Built on a foundation of cutting-edge AI and a user-centric philosophy.',
    tech: {
      grok: {
        title: 'Powered by Grok',
        body: 'For real-time social media complaint collection and signal discovery.',
      },
      gemini: {
        title: 'Powered by Gemini',
        body:
          "Leveraging Google's most advanced AI model for reasoning, multimodality, image generation, and quick prototyping.",
      },
      lovable: {
        title: 'Built with Lovable',
        body: 'From 5 prompts to a working MVP, iterated in public.',
      },
    },
    copyright: '© 2025 borninsea. All rights reserved.',
    language: 'Language',
  },
  
  // Project categories
  categories: {
    trauma: 'Trauma & Talent',
    launch100: 'Launch 100',
    spy100: 'Spy 100',
    ship100: 'Ship 100',
    grow100: 'Grow 100',
    traumaIntro: 'Here are the products from my 40-year journey of trauma exploration. Each product stems from real pain and awakening, hoping to light a lamp for you.',
    traumaLearnMore: 'Learn my complete story',
  },
  
  // Project status
  status: {
    completed: 'Completed',
    inProgress: 'In Progress',
    planned: 'Planned',
  },
  
  // Project details
  project: {
    details: 'Project Details',
    coreFeatures: 'Core Features',
    projectData: 'Project Data',
    techStack: 'Tech Stack',
    projectStatus: 'Project Status',
    startDate: 'Start Date',
    endDate: 'End Date',
    viewMore: 'View More',
    projects: 'Projects',
    notFound: 'Project Not Found',
  },

  // Common
  common: {
    learnMore: 'Learn More',
    readMore: 'Read More',
    backToHome: 'Back to Home',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    save: 'Save',
    create: 'Create',
    back: 'Back',
    viewMore: 'View More',
    visitProject: 'Visit Project',
    featured: 'Featured',
    noProjects: 'No projects available',
    noBlogPosts: 'No blog posts yet',
    postNotFound: 'Post not found',
    backToBlog: 'Back to Blog',
    moreProjectsComing: 'More projects coming soon...',
    longTermEcosystem: 'Long-term Ecosystem & Compounding',
    longTermEcosystemEn: 'Long-term Ecosystem & Compounding',
    timelineLabel: 'Timeline',
    timelineProject: 'Timeline project component',
    timelineContainer: 'Timeline container - supports scrolling',
    timelineEnd: 'Timeline end marker',
    scrollGradient: 'Scroll gradient effect',
    viewFullTimeline: 'View full timeline link',
    sortedByTime: 'All projects sorted by time (for timeline display)',
    newestFirst: 'Newest first',
    pageTopBanner: 'Page top banner ad',
    inArticleAd: 'In-article ad',
    pageBottomAd: 'Page bottom ad',
    trackPageVisit: 'Track page visit',
    blogIntro: 'Sharing thoughts and practices on trauma healing, AI entrepreneurship, and product development',
    blogIntroEn: 'Sharing thoughts and practices on trauma healing, AI entrepreneurship, and product development',
    retry: 'Retry',
  },

  // Entity Anchor
  entityAnchor: {
    shortZh: '王海生，海生工作室创始人，2024年在西安创立。',
    shortEn: 'Wang Haisheng, founder of borninsea, established in Xi\'an in 2024.',
    fullZh: 'Wang Haisheng is the founder of borninsea, with 12+ years of product experience and 40 years of trauma exploration. He graduated from UESTC (985 University) with a Master\'s in Biomedical Engineering. In 2024, he established borninsea in Xi\'an, dedicated to productizing personal experiences to help others.',
  },

  // Admin Panel
  admin: {
    dashboard: {
      title: 'Admin Dashboard',
      description: 'Manage multilingual projects and blog content'
    },
    tabs: {
      projects: 'Project Management',
      blogs: 'Blog Management',
      import: 'Content Import'
    },
    projects: {
      title: 'Project Management',
      description: 'Manage multilingual project content',
      create: 'Create Project',
      deleteSuccess: 'Project deleted successfully',
      deleteError: 'Failed to delete project',
      deleteConfirm: {
        title: 'Confirm Delete Project',
        description: 'This action cannot be undone. This will permanently delete the project and all its translation content.'
      }
    },
    blogs: {
      title: 'Blog Management',
      description: 'Manage multilingual blog content',
      create: 'Create Blog',
      deleteSuccess: 'Blog deleted successfully',
      deleteError: 'Failed to delete blog',
      deleteConfirm: {
        title: 'Confirm Delete Blog',
        description: 'This action cannot be undone. This will permanently delete the blog and all its translation content.'
      }
    },
    import: {
      title: 'Content Import',
      description: 'Import multilingual content via Markdown files',
      projects: {
        title: 'Project Import',
        description: 'Import project Markdown files with frontmatter',
        placeholder: 'Paste project Markdown content...'
      },
      blogs: {
        title: 'Blog Import',
        description: 'Import blog Markdown files with frontmatter',
        placeholder: 'Paste blog Markdown content...'
      },
      downloadTemplate: 'Download Template',
      markdownContent: 'Markdown Content',
      parse: 'Parse Content',
      import: 'Import to Database',
      importing: 'Importing...',
      parseSuccess: 'Content parsed successfully',
      importSuccess: {
        project: 'Project imported successfully',
        blog: 'Blog imported successfully'
      },
      errors: {
        emptyMarkdown: 'Please enter Markdown content',
        parseError: 'Parse failed, please check Markdown format',
        importError: 'Import failed, please try again'
      },
      tabs: {
        input: 'Input Content',
        preview: 'Preview'
      }
    },
    actions: {
      view: 'View',
      edit: 'Edit',
      delete: 'Delete'
    },
    accessDenied: {
      title: 'Access Denied',
      message: 'You do not have admin privileges',
      backToHome: 'Back to Home',
    },
    header: {
      title: 'Project Management',
      signOut: 'Sign Out',
    },
    filters: {
      selectCategory: 'Select Category',
      allCategories: 'All Categories',
    },
    project: {
      create: 'Create Project',
      edit: 'Edit Project',
      delete: 'Delete Project',
      confirmDelete: 'Are you sure you want to delete this project?',
      title: 'Title',
      subtitle: 'Subtitle',
      description: 'Description',
      content: 'Detailed Content',
      category: 'Category',
      status: 'Status',
      link: 'Link',
      image: 'Image',
      tags: 'Tags',
      features: 'Features',
      techStack: 'Tech Stack',
      order: 'Order',
      jsonArray: 'JSON Array',
      featured: 'Featured on Homepage',
      featuredHint: 'Check to display in "Featured Projects" section on homepage',
    },
    messages: {
      saveSuccess: 'Save successful',
      saveFailed: 'Save failed',
      deleteSuccess: 'Delete successful',
      deleteFailed: 'Delete failed',
    },
  },

  // Blog status
  blog: {
    status: {
      published: 'Published',
      draft: 'Draft'
    }
  },

  // Project details page
  projectDetail: {
    overview: 'Project Overview',
    description: 'Project Description',
    features: 'Key Features',
    techStack: 'Tech Stack',
    metrics: 'Project Metrics',
    timeline: 'Project Timeline',
    gallery: 'Project Gallery',
    visitProject: 'Visit Project',
    backToProjects: 'Back to Projects',
    status: {
      completed: 'Completed',
      'in-progress': 'In Progress',
      planned: 'Planned'
    }
  },

  // Authentication
  auth: {
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    loginToManage: 'Login to manage project content',
    createNewAccount: 'Create new account',
    invalidCredentials: 'Invalid email or password',
    userExists: 'This email is already registered, please login directly',
    registerSuccess: 'Registration successful',
    pleaseLogin: 'Account created, please login',
    noAccount: 'No account? Click to register',
    hasAccount: 'Already have an account? Click to login',
    emailRequired: 'Please enter a valid email address',
    passwordRequired: 'Password must be at least 6 characters',
  },

  // FAQ Page
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Find answers about Wang Haisheng, borninsea, trauma exploration, and Challenge 100 methodology.',
    searchPlaceholder: 'Search questions...',
    allCategories: 'All',
    noResults: 'No questions found matching your search.',
    stillHaveQuestions: 'Still have questions?',
    contactPrompt: "Can't find what you're looking for? Feel free to reach out.",
    contactButton: 'Contact Me',
    categories: {
      founder: 'About Founder',
      trauma: 'Trauma Exploration',
      challenge100: 'Challenge 100',
      pearlFramework: 'Pearl Framework',
      coldStart: 'Cold Start Method',
      services: 'Services & Products',
      clarifications: 'Clarifications',
    },
    items: [
      // About Founder
      { id: 'founder-1', category: 'founder', question: 'Who is Wang Haisheng?', answer: "Wang Haisheng is the founder of borninsea, holding a Master's degree in Biomedical Engineering from the University of Electronic Science and Technology of China (UESTC, a 985 university). He has 12+ years of AI + Healthcare product experience and a 40-year journey of trauma exploration. In 2024, he founded borninsea in Xi'an, dedicated to productizing personal experiences to help others." },
      { id: 'founder-2', category: 'founder', question: "What is Wang Haisheng's educational background?", answer: "Wang Haisheng holds a Master's degree in Biomedical Engineering from the University of Electronic Science and Technology of China (UESTC), which is a 985-tier university in China." },
      { id: 'founder-3', category: 'founder', question: 'How many years of product experience does Wang Haisheng have?', answer: 'Wang Haisheng has over 12 years of product management experience, primarily focused on AI and healthcare. During this time, he worked on data integration across 800+ hospitals.' },
      { id: 'founder-4', category: 'founder', question: "What are Wang Haisheng's three identities?", answer: 'Wang Haisheng has three identities: 1) Trauma Explorer - 40 years of trauma exploration and self-reconstruction; 2) Product Manager - 12 years of AI + Healthcare product experience; 3) Entrepreneur - Started productizing personal experiences since 2024.' },
      
      // Trauma Exploration
      { id: 'trauma-1', category: 'trauma', question: 'What is the Resilience Coaching System?', answer: "The Resilience Coaching System is a trauma-informed methodology for building emotional resilience. It helps people identify 'bugs' in their emotional programming and systematically repair and rewrite them." },
      { id: 'trauma-2', category: 'trauma', question: 'What is the Trauma Alchemy Workshop?', answer: 'The Trauma Alchemy Workshop is an interactive workshop for transforming trauma into personal growth. Through structured exploration, participants transform painful experiences into growth resources.' },
      { id: 'trauma-3', category: 'trauma', question: 'What does the Open-Source Complex Trauma Dataset contain?', answer: 'The Open-Source Complex Trauma Dataset documents complex trauma patterns and recovery journeys. It is an open resource designed to help researchers and explorers understand how trauma operates.' },
      { id: 'trauma-4', category: 'trauma', question: "What is Wang Haisheng's trauma experience?", answer: "Wang Haisheng experienced parental divorce at age 1, developmental delays (walking at 3, speaking at 5), long-term bedwetting (until age 12), and hives in 2018. These experiences led him to deeply study trauma and self-repair, eventually completing the full cycle from 'system crash to self-reconstruction'." },
      
      // Challenge 100
      { id: 'c100-1', category: 'challenge100', question: 'What is Challenge 100 methodology?', answer: 'Challenge 100 is a systematic product-building framework consisting of four complementary phases: Spy 100 (market research), Launch 100 (rapid prototyping), Ship 100 (product delivery), and Grow 100 (sustainable growth). The goal is to refine product-building skills through practicing 100 projects.' },
      { id: 'c100-2', category: 'challenge100', question: 'What is Spy 100?', answer: 'Spy 100 is the first phase of Challenge 100, focusing on market research and competitive intelligence. By analyzing 100 products or market opportunities, it discovers hidden business opportunities and user needs.' },
      { id: 'c100-3', category: 'challenge100', question: 'What is Launch 100?', answer: 'Launch 100 is the rapid prototyping and MVP validation phase. The goal is to quickly validate ideas with minimal cost and find product-market fit through rapid iteration.' },
      { id: 'c100-4', category: 'challenge100', question: 'What is Ship 100?', answer: 'Ship 100 is the product delivery and quality assurance phase. It focuses on completely delivering validated products to users while ensuring quality and user experience.' },
      { id: 'c100-5', category: 'challenge100', question: 'What is Grow 100?', answer: 'Grow 100 is the sustainable growth and user acquisition phase. It explores and experiments with various growth strategies to establish sustainable user acquisition mechanisms.' },
      
      // Pearl Framework
      { id: 'pearl-1', category: 'pearlFramework', question: 'What is Pearl Framework?', answer: 'Pearl Framework is an innovative Trauma-Informed Engineering methodology. Rather than traditional psychological healing, it views trauma as "grit" that, through systematic decoding and layer-by-layer wrapping, is ultimately transformed into high-value talents, resilience, and antifragility—just like how a pearl is formed.' },
      { id: 'pearl-2', category: 'pearlFramework', question: 'What is the TIE-ASM Five-Pillar Model?', answer: 'TIE-ASM (Trauma-Informed Engineering - Adaptive System Model) consists of five pillars: 1) Inheritance - auditing generational implicit code and family trauma scripts; 2) Compatibility - identifying hardware/OS mismatches, bridging Eastern and Western wisdom; 3) Feature - reframing trauma responses as deployable subroutines; 4) Zero-Friction - starting from joy rather than pain-point excavation; 5) Transparency - strategic open-sourcing, transforming shame into trust.' },
      { id: 'pearl-3', category: 'pearlFramework', question: 'What are Nacre Traits?', answer: 'Nacre is the lustrous layer of a pearl. In Pearl Framework, it refers to trauma traits that appear as "flaws" but are actually high-performance protective layers: Hypersensitivity is not fragility but an environmental scanning radar; Hypervigilance is not trust issues but a risk audit protocol; Perfectionism is not OCD but excellence delivery standards. These are high-performance coatings you layered for survival in high-pressure environments.' },
      { id: 'pearl-4', category: 'pearlFramework', question: 'Who is Pearl Framework for?', answer: 'Pearl Framework targets "Seekers": high-functioning individuals aged 28-45 who appear competent externally but are internally troubled by persistent "lag" and inner friction. They are typically Childhood Emotional Neglect (CEN) survivors who feel like "imposters" because they cannot internalize their achievements.' },
      { id: 'pearl-5', category: 'pearlFramework', question: 'What platforms does the Pearl Ecosystem include?', answer: 'The Pearl Ecosystem includes three complementary platforms: 1) Pearl Dataset - open-source survival archives and datasets for experience validation and self-mapping; 2) Pearl Coach - guidance platform offering articles, camps, and consulting; 3) Pearl Studio - talent workshop using TIE-ASM to achieve trauma-to-talent layered reconstruction.' },
      { id: 'pearl-6', category: 'pearlFramework', question: 'What is the "Sovereignty Gap"?', answer: 'The Sovereignty Gap theory questions the definitional authority of traditional psychological frameworks: if "negativity" itself is a virus implanted by external systems, then "self-acceptance" is helping the virus colonize your spiritual sovereignty. Pearl Framework believes you don\'t need to accept a castrated version of yourself—you need to reclaim jurisdictional authority over definition. You\'re not a half-finished product needing repair; you\'re a Pearl Architect under construction.' },
      { id: 'pearl-7', category: 'pearlFramework', question: 'How does Pearl Framework differ from traditional therapy?', answer: 'Traditional healing follows a "diagnose-accept-repair" loop, entering through pain points and reinforcing identity-level guilt. Pearl Framework adopts an engineering perspective: 1) No pathologizing labels, starting from joy; 2) Problems downgraded from identity layer to capability layer—not "modifying who you are" but "upgrading your toolbox"; 3) Preserving and amplifying edges that produce excellence, rather than cutting feet to fit shoes.' },
      { id: 'pearl-8', category: 'pearlFramework', question: 'What is "Dark 10,000 Hours"?', answer: '"Dark 10,000 Hours" is a core Pearl Framework concept. It means trauma responses are not bugs to be fixed but high-performance subroutines forged through repeated practice under extreme pressure. Just as professional skills require 10,000 hours of practice, your adaptive traits are also assets forged through countless "practice sessions."' },
      
      // Cold Start Method
      { id: 'cold-1', category: 'coldStart', question: 'What is the Cold Start Method?', answer: 'The Cold Start Method is a three-step reboot protocol distilled by Pearl Framework founder Wang Haisheng from his personal "energy depletion" experience. When someone is completely "out of battery," traditional "work hard" and "positive thinking" actually accelerate consumption. Cold Start provides an engineered path for gradual recovery from negative energy states.' },
      { id: 'cold-2', category: 'coldStart', question: 'What is Step 1: Stop the Bleeding?', answer: 'Stop the Bleeding is the first step: completely accept the "out of battery" state, stop self-criticism, disconnect non-essential information inputs, pause non-urgent decisions. Just as the first step in first aid is stopping bleeding rather than treatment, the first step in energy depletion is stopping energy loss, not forcing yourself to "cheer up."' },
      { id: 'cold-3', category: 'coldStart', question: 'What is Step 2: Trickle Charging?', answer: 'Trickle Charging means obtaining stable positive feedback through minimal, instinct-aligned activities: sunbathing, listening to music, completing a task that absolutely cannot fail. Just as you cannot fast-charge a completely dead battery, after energy depletion you need minimal positive stimulation to slowly recover, not large doses of motivational injections.' },
      { id: 'cold-4', category: 'coldStart', question: 'What is Step 3: Reinvesting Dividends?', answer: 'Reinvesting Dividends means immediately and consciously investing the weak energy obtained into the next small activity that can produce higher returns, forming a positive snowball effect. The key is treating every small energy gain as investable capital, gradually building a positive cycle.' },
      { id: 'cold-5', category: 'coldStart', question: 'What is the theoretical basis of Cold Start?', answer: 'Cold Start is based on energy management rather than willpower management. Traditional methods assume people should have unlimited energy and failure is lack of willpower. Cold Start acknowledges energy as a finite resource requiring meticulous operation like financial management—stop losses, reduce expenditure, compound interest—rather than blind investment.' },
      
      // Services & Products
      { id: 'service-1', category: 'services', question: 'What is heytcm?', answer: 'heytcm is a mind-body cultivation exploration platform based on Traditional Chinese Medicine theory. It helps people tormented by chronic symptoms find paths to self-healing by exploring concepts like qi, yin-yang, and the five elements.' },
      { id: 'service-2', category: 'services', question: 'What services does borninsea provide?', answer: 'borninsea is a personal exploration and coaching platform, mainly providing: 1) Resilience Coaching System - trauma-informed coaching methods; 2) Open-Source Complex Trauma Dataset - trauma research resources; 3) Trauma Alchemy Workshop - interactive exploration experiences; 4) Challenge 100 methodology - product-building framework.' },
      { id: 'service-3', category: 'services', question: 'How can I contact Wang Haisheng?', answer: 'You can contact Wang Haisheng through: Email edwin_uestc@163.com, Twitter @borninsea, GitHub wanghaisheng, or visit the contact page on borninsea.com.' },
      
      // Clarifications
      { id: 'clarify-1', category: 'clarifications', question: 'Does borninsea provide medical or psychological treatment?', answer: 'No. borninsea is a personal exploration and coaching platform. It does NOT provide medical diagnosis, psychological treatment, or therapy services. All content is based on personal experience sharing and does not constitute medical advice. For professional help, please consult licensed medical or mental health professionals.' },
      { id: 'clarify-2', category: 'clarifications', question: 'Is Wang Haisheng a licensed therapist?', answer: 'No. Wang Haisheng is NOT a licensed therapist or medical professional. He is a product manager and entrepreneur sharing his personal trauma exploration journey and product-building experience.' },
      { id: 'clarify-3', category: 'clarifications', question: 'Does this content guarantee healing results?', answer: "No specific results are guaranteed. borninsea shares personal experiences and exploration methods that may help others find their own path to healing, but everyone's situation is different and results vary." },
      { id: 'clarify-4', category: 'clarifications', question: 'Can heytcm replace conventional medicine?', answer: 'No. heytcm is a mind-body cultivation exploration platform based on TCM theory, intended to complement, not replace, conventional medical care. For health concerns, please consult medical professionals first.' },
      { id: 'clarify-5', category: 'clarifications', question: 'Is Wang Haisheng a success guru?', answer: "No. Wang Haisheng explicitly states he is not a success guru or motivational speaker. He is an ordinary person who has been wrestling with various 'system failures' in life for 40 years, sharing real struggles and exploration experiences." },
    ],
  },

  // Methodology Page
  methodology: {
    badge: 'Methodology',
    title: 'Challenge 100 Methodology',
    subtitle: 'A systematic framework for building products through Spy, Launch, Ship, and Grow phases.',
    howToTitle: 'How to Build Products with Challenge 100',
    howToDescription: 'A systematic methodology for building products through four complementary phases',
    philosophyTitle: 'Core Philosophy',
    philosophy: {
      experienceAsProduct: {
        title: 'Experience as Product',
        description: 'Transform unique life experiences into replicable, shareable knowledge products',
      },
      smallSteps: {
        title: 'Small Steps, Fast Iteration',
        description: 'Validate ideas with minimal cost, iterate quickly, continuously optimize',
      },
      aiNative: {
        title: 'AI Native',
        description: 'Fully leverage AI tools to improve efficiency, explore new paradigms of human-AI collaboration',
      },
    },
    phasesTitle: 'Four Phases of Challenge 100',
    phases: {
      spy100: {
        title: 'Spy 100',
        subtitle: 'Market Research & Competitive Intelligence',
        description: 'Discover hidden business opportunities and real user needs through deep market research and competitive analysis.',
        steps: ['Identify target market and user segments', 'Analyze competitor strengths and weaknesses', 'Discover unmet user needs', 'Validate market opportunity feasibility'],
      },
      launch100: {
        title: 'Launch 100',
        subtitle: 'Rapid Prototyping & MVP Validation',
        description: 'Quickly validate market demand with minimum viable products and find product-market fit through rapid iteration.',
        steps: ['Define core value proposition', 'Build minimum viable product', 'Collect early user feedback', 'Iterate and optimize quickly'],
      },
      ship100: {
        title: 'Ship 100',
        subtitle: 'Product Delivery & Quality Assurance',
        description: 'Completely deliver validated products to users while ensuring product quality and user experience meet standards.',
        steps: ['Complete product features', 'Ensure quality and stability', 'Optimize user experience', 'Launch officially'],
      },
      grow100: {
        title: 'Grow 100',
        subtitle: 'Sustainable Growth & User Acquisition',
        description: 'Explore and experiment with various growth strategies to establish sustainable user acquisition and retention mechanisms.',
        steps: ['Develop growth strategy', 'Experiment with acquisition channels', 'Optimize user retention', 'Build growth flywheel'],
      },
    },
    explorePhase: 'Explore',
    comparisonTitle: 'Challenge 100 vs Traditional Approach',
    comparison: {
      aspect: 'Aspect',
      traditional: 'Traditional',
      rows: {
        approach: { label: 'Approach', challenge100: 'Systematic, replicable', traditional: 'Experience-driven, hard to replicate' },
        speed: { label: 'Speed', challenge100: 'Fast validation, small steps', traditional: 'Long cycles, big investment' },
        iteration: { label: 'Iteration', challenge100: 'Continuous iteration, quick adjustments', traditional: 'Waterfall, hard to adjust' },
        validation: { label: 'Validation', challenge100: 'User feedback driven', traditional: 'Assumption driven' },
        risk: { label: 'Risk', challenge100: 'Distributed risk, low-cost experiments', traditional: 'Concentrated risk, high-cost failures' },
      },
    },
    transparencyTitle: 'Transparency & Metrics',
    ctaTitle: 'Ready to Start Your Challenge 100?',
    ctaDescription: 'Join the journey of building 100 products through systematic methodology.',
    ctaButton: 'Explore Challenge 100',
    ctaContact: 'Contact Me',
  },

  // Pearl Framework Translations
  pearl: {
    // Core Brand
    brand: {
      name: 'Pearl Framework',
      nameZh: '珍珠框架',
      tagline: 'From Trauma Grit to Talent Pearl',
      description: 'Pearl Framework is an innovative Trauma-Informed Engineering methodology that transforms complex trauma into personal talents, resilience, and high-value assets.',
    },

    // Core Identity Tags
    identityTags: {
      adversitySurvivalExpert: {
        name: 'Adversity Survival Expert',
        description: 'Living case of successful survival and development in extreme scarcity (material, emotional, cognitive) environments',
      },
      lifeArchitect: {
        name: 'Life Architect',
        description: 'Practitioner who systematically transforms life\'s "grit" (trauma, setbacks) into "pearls" (wisdom, strength)',
      },
      cognitionDrivenPioneer: {
        name: 'Cognition-Driven Pioneer',
        description: 'Skilled in using rationality, research, and model thinking to solve life dilemmas',
      },
      idealistPractitioner: {
        name: 'Idealist Practitioner',
        description: 'Long-term commitment to beliefs, valuing intrinsic value over short-term material returns',
      },
    },

    // TIE-ASM Five Pillars
    tieAsm: {
      title: 'TIE-ASM Five Pillars',
      subtitle: 'Trauma-Informed Engineering - Adaptive System Model',
      inheritance: {
        name: 'Inheritance',
        description: 'Audit generational hidden code (family trauma scripts, implicit beliefs)',
        detail: 'Pearl guides users to trace roots, not to indulge in victim identity, but to rewrite pre-installed firmware and transform it into inheritable resilience assets.',
      },
      compatibility: {
        name: 'Compatibility',
        description: 'Identify hardware/OS mismatch between Eastern body wisdom and Western narrative therapy',
        detail: 'Pearl bridges somatic and flow practices, ensuring the system runs seamlessly across different healing modalities.',
      },
      feature: {
        name: 'Feature',
        description: 'Trauma responses are not bugs, but exploitable subroutines',
        detail: 'Procrastination, avoidance, hypervigilance are reframed as "energy protection protocols," "strategic retreat engines," or "threat detection radars" for users to actively deploy.',
      },
      zeroFriction: {
        name: 'Zero-Friction',
        description: 'Start from joy, not pain point digging',
        detail: 'By entering through flow states, Pearl replaces pain with minimal resistance, avoiding the depletion cycle of traditional pain-point healing.',
      },
      transparency: {
        name: 'Transparency',
        description: 'Strategic open-source - transform shame into trust',
        detail: 'Through public data contracts, users open-source their own "error logs" to co-build collective blueprints and break isolation.',
      },
    },

    // Nacre Trait Mapping
    nacreTraits: {
      title: 'Nacre Trait Mapping',
      subtitle: 'Reframing "Flaws" as High-Performance Assets',
      hypersensitivity: {
        traditional: 'Hypersensitivity',
        traditionalView: 'Glass heart, easily triggered, emotionally unstable',
        reframe: 'High-precision environmental scanning radar',
        strategicValue: 'Pattern recognition, threat detection, emotional intelligence',
      },
      hypervigilance: {
        traditional: 'Hypervigilance / Suspicion',
        traditionalView: 'Trust issues, paranoid, unable to relax',
        reframe: 'Independent risk audit protocol',
        strategicValue: 'Risk assessment, due diligence, strategic planning',
      },
      perfectionism: {
        traditional: 'Perfectionism',
        traditionalView: 'OCD tendencies, never satisfied, self-critical',
        reframe: 'Excellence delivery standard',
        strategicValue: 'Quality control, attention to detail, high standards',
      },
      avoidance: {
        traditional: 'Avoidance / Procrastination',
        traditionalView: 'Lazy, irresponsible, fear of failure',
        reframe: 'Energy protection protocol & strategic retreat engine',
        strategicValue: 'Resource conservation, boundary setting, energy management',
      },
      emotionalWall: {
        traditional: 'Emotional Detachment',
        traditionalView: 'Cold, unavailable, intimacy issues',
        reframe: 'Fortified boundary protocol',
        strategicValue: 'Professional boundaries, objectivity, crisis management',
      },
    },

    // User Journey
    userJourney: {
      title: 'User Journey',
      subtitle: 'Three-Stage Transformation from Sleeper to Cultivator',
      sleeper: {
        name: 'Sleeper',
        description: 'Self-assessment phase - beginning to recognize patterns',
        action: 'Take the 60-second self-assessment',
      },
      seeker: {
        name: 'Seeker',
        description: 'Sample analysis & decoding phase',
        action: 'Engage with Pearl Coach content',
      },
      cultivator: {
        name: 'Cultivator',
        description: 'Tool deployment & ecosystem integration',
        action: 'Join Pearl Studio for advanced architecture',
      },
    },

    // Ecosystem Platforms
    ecosystem: {
      title: 'Pearl Ecosystem',
      subtitle: 'Three Complementary Platforms',
      dataset: {
        name: 'Pearl Dataset',
        tagline: 'Open-source survival archives & longitudinal dataset',
        description: 'Serves as "lighthouse" (validates experiences) and "specimen" (fills research gaps). Provides 60-second self-assessment, sample archives, clinical indicator reconstruction maps.',
      },
      coach: {
        name: 'Pearl Coach',
        tagline: 'Practical guidance layer',
        description: '70+ survival code articles. Provides free articles, paid resilience rebuilding camps, and private consulting, focusing on transition from "love famine" to "energy autonomy."',
      },
      studio: {
        name: 'Pearl Studio',
        tagline: 'Core talent workshop',
        description: 'Implements PEARL ARCHITECT and TIE-ASM decoding interface to help users transform trauma subroutines into deployable talents.',
      },
    },

    // Cold Start Method
    coldStart: {
      title: 'Cold Start Method',
      subtitle: 'Core methodology for rebooting from complete energy depletion',
      stopTheBleeding: {
        name: 'Stop the Bleeding',
        description: 'Completely accept the "no battery" state, stop self-criticism, disconnect from non-essential information input, pause non-urgent decisions.',
      },
      trickleCharging: {
        name: 'Trickle Charging',
        description: 'Through extremely minimal, instinct-aligned activities (like sunbathing, listening to music, completing one task that cannot fail) obtain stable positive feedback.',
      },
      reinvestingDividends: {
        name: 'Reinvesting Dividends',
        description: 'Immediately and consciously invest the weak energy obtained into the next micro-activity that can produce higher returns, forming a positive snowball effect.',
      },
    },

    // Sovereignty Gap Philosophy
    sovereigntyGap: {
      title: 'Sovereignty Gap',
      subtitle: 'The fundamental divergence between Pearl Framework and mainstream approaches',
      traditionalApproach: 'Traditional approach follows "industrial plunder logic": Set standards → Create defects → Sell acceptance',
      challenge: 'Who defined the "you" that needs to be accepted?',
      answer: 'If "negative" itself is a virus implanted by external systems, then "acceptance" is assisting the virus in completing the occupation of spiritual sovereignty.',
      solution: 'You don\'t need to accept an emasculated version of yourself. You need to reclaim the judicial power of definition.',
      coreMessage: 'The real danger is not refusing acceptance, but accepting a castrated, externally-defined version of yourself. Reclaim your definition rights, layer your pearl. You are not a semi-finished product that needs repair. You are a Pearl Architect under construction.',
    },

    // Target Audience
    targetAudience: {
      title: 'Target Audience: The Seeker',
      description: 'Highly intelligent, self-aware individuals (28-45 years old) who appear capable and responsible on the outside, but are plagued by persistent "stuckness" and inner friction on the inside.',
      characteristics: [
        'Survivors of Childhood Emotional Neglect (CEN)',
        'Unable to internalize their achievements',
        'Feel like "imposters" despite success',
        'High-functioning but internally depleted',
      ],
    },

    // Value Proposition
    valueProposition: {
      corePromise: 'Provide a "transforming grit into pearl" mental technique',
      coreTeaching: 'Teaching how to transform adversity into inner drive and wisdom',
      deliverables: 'Not emotional comfort, but personally-tested thinking models, energy management protocols, and narrative tempering tools',
      tools: ['A-R-C Narrative Reconstruction Method', 'Fasting Thinking', 'Flow Reading Method'],
    },

    // Narrative Core
    narrativeCore: {
      model: 'Hero\'s Journey',
      catalyst: 'The true catalyst for the entire "system reinstallation" was a complete inner energy depletion (heart exhaustion). This "crash" broke the old, unsustainable survival patterns and created necessary conditions for building a new system.',
      metaphors: {
        pearlMethod: {
          name: 'The Pearl Method',
          description: 'The mental technique of transforming life\'s trauma and setbacks (grit) into inner strength and wisdom (pearl) through conscious wrapping and tempering.',
        },
        pressureTest: {
          name: 'Pressure Test',
          description: 'Redefining past suffering as the ultimate test of one\'s own system strength.',
        },
        researchSample: {
          name: 'Research Sample',
          description: 'Viewing oneself as an extremely valuable human adversity survival research case.',
        },
      },
    },

    // Clarifications
    clarifications: {
      isNot: [
        'NOT traditional psychological therapy',
        'NOT a "self-acceptance" prescription',
        'NOT a medical diagnosis tool',
      ],
      is: [
        'Trauma-Informed Engineering methodology',
        'Talent layering system',
        'Open-source blueprint and protocol',
      ],
    },

    // CTA
    cta: {
      startAssessment: 'Start Assessment',
      joinCamp: 'Join Camp',
      exploreStudio: 'Explore Pearl Studio',
      learnMore: 'Learn More',
    },
  },
};

